import { ColorCondition, ConditionOperator, ImageCondition, TextCondition, ThemeVariableType } from './types';
import { parseThemeVariableValue } from './themeVariables';
import { labelVariables } from './variables';

const normalizeConditionType = (type?: string | ThemeVariableType): ThemeVariableType => {
  if (type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';
  return 'string';
};

/**
 * Build a lookup of condition variableKey -> ThemeVariableType, merging the
 * template's theme variables with the built-in component variable catalog
 * (labelVariables). Theme variables take precedence on key collision, matching
 * the ordering used by the WYSIWYG editor.
 */
export const buildConditionTypeByKey = (
  themeVariables?: Array<{ key?: string; type?: string | ThemeVariableType }> | null,
): Record<string, ThemeVariableType> => {
  const acc: Record<string, ThemeVariableType> = {};
  if (themeVariables) {
    for (const v of themeVariables) {
      if (v?.key && !acc[v.key]) acc[v.key] = normalizeConditionType(v.type as any);
    }
  }
  for (const v of labelVariables) {
    if (v?.key && !acc[v.key]) acc[v.key] = normalizeConditionType(v.type as any);
  }
  acc['ingredients.totalActiveCannabinoids.percent'] ??= 'number';
  return acc;
};

type TypeLookup = Record<string, ThemeVariableType | undefined>;

const coerce = (value: unknown, type?: ThemeVariableType): string | number | boolean | undefined => {
  if (type === 'boolean') return parseThemeVariableValue(value as any, 'boolean');
  if (type === 'number') return parseThemeVariableValue(value as any, 'number');
  return typeof value === 'string' ? value : value !== undefined && value !== null ? String(value) : undefined;
};

export const matchesCondition = (
  value: unknown,
  condition: { operator: ConditionOperator; value?: string },
  type?: ThemeVariableType,
) => {
  const operator = condition.operator;
  const compareRaw = condition.value ?? '';
  const coercedValue = coerce(value, type);

  if (operator === 'truthy') return Boolean(coercedValue);
  if (operator === 'falsy') return !coercedValue;

  if (operator === 'isEmpty') {
    return coercedValue === undefined || coercedValue === null || coercedValue === '';
  }
  if (operator === 'isNotEmpty') {
    return coercedValue !== undefined && coercedValue !== null && coercedValue !== '';
  }

  if (coercedValue === undefined || coercedValue === null) return false;

  const compare = coerce(compareRaw, type);

  switch (operator) {
    case 'equals':
      if (typeof coercedValue === 'string' && typeof compare === 'string') {
        return coercedValue.toLowerCase() === compare.toLowerCase();
      }
      return coercedValue === compare;
    case 'notEquals':
      if (typeof coercedValue === 'string' && typeof compare === 'string') {
        return coercedValue.toLowerCase() !== compare.toLowerCase();
      }
      return coercedValue !== compare;
    case 'contains':
      return typeof coercedValue === 'string' && typeof compare === 'string' && coercedValue.toLowerCase().includes(compare.toLowerCase());
    case 'gt':
      return typeof coercedValue === 'number' && typeof compare === 'number' && coercedValue > compare;
    case 'lt':
      return typeof coercedValue === 'number' && typeof compare === 'number' && coercedValue < compare;
    case 'gte':
      return typeof coercedValue === 'number' && typeof compare === 'number' && coercedValue >= compare;
    case 'lte':
      return typeof coercedValue === 'number' && typeof compare === 'number' && coercedValue <= compare;
    default:
      return false;
  }
};

export const findFirstMatchingCondition = <T extends { variableKey: string; operator: ConditionOperator }>(
  conditions: T[] | undefined,
  data: Record<string, unknown>,
  typeLookup: TypeLookup,
): T | undefined => {
  if (!conditions || !conditions.length) return undefined;
  for (const condition of conditions) {
    const type = typeLookup[condition.variableKey];
    const value = data ? data[condition.variableKey] : undefined;
    if (matchesCondition(value, condition, type)) return condition;
  }
  return undefined;
};

export const defaultOperatorForType = (type: ThemeVariableType): ConditionOperator => {
  if (type === 'boolean') return 'equals';
  if (type === 'number') return 'equals';
  return 'equals';
};

export const operatorsForType = (type: ThemeVariableType): ConditionOperator[] => {
  if (type === 'boolean') return ['equals', 'notEquals', 'truthy', 'falsy'];
  if (type === 'number') return ['equals', 'notEquals', 'gt', 'lt', 'gte', 'lte'];
  return ['equals', 'notEquals', 'contains', 'isEmpty', 'isNotEmpty'];
};

export const operatorLabels: Record<ConditionOperator, string> = {
  equals: 'Equals',
  notEquals: 'Does not equal',
  contains: 'Contains',
  gt: 'Greater than',
  lt: 'Less than',
  gte: 'Greater than or equal to',
  lte: 'Less than or equal to',
  truthy: 'Is true',
  falsy: 'Is false',
  isEmpty: 'Is empty',
  isNotEmpty: 'Is not empty',
};

export type TextConditionMatch = TextCondition | undefined;
export type ImageConditionMatch = ImageCondition | undefined;
export type ColorConditionMatch = ColorCondition | undefined;
